'use client';

import { useEffect, useRef, useState } from 'react';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import { MARKERS, MAX_DEPTH, shadeAt, zoneAt } from './deepSeaData';
const BEST = bestKey('deep');

/** Screen pixels per metre of ocean. Tuned so the dive takes a real scroll. */
const PIXELS_PER_METRE = 1.4;
/** Room below the deepest marker so its card is readable, not jammed to the edge. */
const FLOOR_PADDING = 160;
const TRACK_HEIGHT = MAX_DEPTH * PIXELS_PER_METRE + FLOOR_PADDING;
const DeepSea = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [depth, setDepth] = useState(0);
  const best = useLocalBest(BEST);
  const {
    record
  } = useGameTracking();

  /* Deepest point reached, kept in a ref so the scroll handler never needs to
   * re-subscribe and the flush interval can be created once. */
  const deepestRef = useRef(0);
  const recordRef = useRef(record);
  recordRef.current = record;

  /* Scroll → depth. Read in a rAF so a fast flick coalesces into one state
   * update per frame instead of one per scroll event, which is what keeps this
   * smooth on a trackpad. */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;

        /* Depth comes from scroll *progress*, not raw scrollTop. The maximum
         * scrollTop is `scrollHeight - clientHeight`, so dividing scrollTop by
         * pixels-per-metre tops out one viewport short of the sea floor — the
         * readout could never reach Challenger Deep even scrolled fully down. */
        const scrollable = track.scrollHeight - track.clientHeight;
        const progress = scrollable <= 0 ? 0 : track.scrollTop / scrollable;
        const metres = Math.round(Math.min(1, Math.max(0, progress)) * MAX_DEPTH);
        setDepth(metres);
        deepestRef.current = Math.max(deepestRef.current, metres);
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);

  // Report the deepest point periodically and on leaving.
  useEffect(() => {
    const flush = () => {
      if (deepestRef.current > 0) {
        recordRef.current('deep', deepestRef.current);
      }
    };
    const id = setInterval(flush, 10_000);
    return () => {
      clearInterval(id);
      flush();
    };
  }, []);
  const zone = zoneAt(depth);
  const nextMarker = MARKERS.find(marker => marker.depth > depth);
  return <div>
      {/* Readout sits outside the scroller so it never moves. */}
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <p className="text-4xl font-bold tabular-nums text-textPrimary">
            {depth.toLocaleString()}
            <span className="ml-1 text-lg font-normal text-textSecondary">
              m
            </span>
          </p>
          <p className="text-sm font-emphasis text-textPrimary">{zone.name}</p>
          <p className="text-xs text-textSecondary">{zone.note}</p>
        </div>
        <p className="text-right text-xs text-textGray">
          {nextMarker ? `Next: ${(nextMarker.depth - depth).toLocaleString()} m to go` : 'You are at the bottom.'}
        </p>
      </div>

      {/* The dive. Scrolling this container is the whole interaction. */}
      <div ref={trackRef} tabIndex={0} aria-label="Scroll down to dive deeper into the ocean" className="relative h-[60vh] overflow-y-auto overscroll-contain rounded-2xl border border-ds-border-standard" style={{
      backgroundColor: shadeAt(depth)
    }}>
        <div className="relative" style={{
        height: TRACK_HEIGHT
      }}>
          {MARKERS.map(marker_0 => <div key={marker_0.depth} className="absolute inset-x-0 px-5" style={{
          top: marker_0.depth * PIXELS_PER_METRE
        }}>
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="text-3xl">
                  {marker_0.emoji}
                </span>
                <div className="min-w-0 max-w-md rounded-xl bg-black/35 p-3 backdrop-blur-sm">
                  <p className="text-sm font-emphasis text-white">
                    {marker_0.name}
                    <span className="ml-2 font-normal tabular-nums text-white/60">
                      {marker_0.depth.toLocaleString()} m
                    </span>
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-white/80">
                    {marker_0.fact}
                  </p>
                </div>
              </div>
            </div>)}

          {/* Depth ruler every 500 m, so the emptiness is legible. */}
          {Array.from({
          length: Math.floor(MAX_DEPTH / 500)
        }, (_, i) => {
          const metres_0 = (i + 1) * 500;
          return <div key={`rule-${metres_0}`} className="absolute inset-x-0 border-t border-white/10" style={{
            top: metres_0 * PIXELS_PER_METRE
          }}>
                <span className="ml-2 text-[10px] tabular-nums text-white/30">
                  {metres_0.toLocaleString()} m
                </span>
              </div>;
        })}
        </div>
      </div>

      <p className="mt-3 text-xs text-textGray">
        Scroll inside the panel to dive. Depths and creatures are real; the
        deepest figures are records rather than typical depths.
      </p>

      {best !== null && <p className="mt-2 text-sm text-textGray">
          Deepest you have reached: {Number(best).toLocaleString()} m
        </p>}
    </div>;
};
export default DeepSea;